// src/pages/Profil.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";
import { notify } from "../ui/notify";
import InseeAutocomplete from "../components/InseeAutocomplete";
import BoutonOnglet from "../components/BoutonOnglet";

/* ---------------- Models ---------------- */
const initialUser = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address: { line1: "", line2: "", zip: "", city: "", country: "FR" },
};

const initialCompany = {
  nom: "",
  siret: "",
  ape: "",
  tva_intra: "",
  forme_juridique: "",
  siege: { line1: "", line2: "", zip: "", city: "", country: "FR" },
  billing: { line1: "", line2: "", zip: "", city: "", country: "FR" },
};

/* ---------------- Helpers ---------------- */
const onObj = (setter, key, value) => setter((s) => ({ ...s, [key]: value }));
const onSub = (setter, sub, key, value) =>
  setter((s) => ({ ...s, [sub]: { ...s[sub], [key]: value } }));

const primaryBtn =
  "inline-flex items-center justify-center h-9 px-4 rounded-full bg-sky-700 " +
  "text-white text-sm font-medium shadow-sm hover:bg-sky-600 " +
  "disabled:opacity-60 disabled:cursor-default";

const dangerBtn =
  "inline-flex items-center justify-center h-9 px-4 rounded-full border border-red-500 " +
  "text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-60 disabled:cursor-default";

/** Large liste de formes juridiques (libres + datalist) */
const FORMES_JURIDIQUES = [
  "Entreprise individuelle",
  "Micro-entreprise",
  "EI (Entreprise individuelle)",
  "EIRL",
  "EURL",
  "SARL",
  "SASU",
  "SAS",
  "SA",
  "SCA",
  "SNC",
  "SCS",
  "SCI",
  "SCM",
  "SCOP",
  "SCIC",
  "SCEA",
  "SELARL",
  "SELAS",
  "SELASU",
  "SELARLU",
  "SELAFA",
  "SPE (Société Pluri-professionnelle d’Exercice)",
  "Association loi 1901",
  "Fondation",
  "GIE",
  "GIP",
  "Syndicat",
  "Auto-entrepreneur",
  "Profession libérale",
  "Exploitant agricole",
  "EARL",
  "GAEC",
  "Société civile",
  "Société d’exercice libéral",
  "Société en participation",
];

/* ---------------- Sous-composants ---------------- */

/** Champ adresse avec auto-villes par code postal (via /api/geo/communes) */
function AdresseBloc({ label, value, onChange, lock = false }) {
  const [villes, setVilles] = useState([]);
  const zip = value?.zip?.trim() || "";

  useEffect(() => {
    let cancel = false;
    async function run() {
      setVilles([]);
      if (!zip || zip.length < 4) return;
      try {
        const { data } = await axiosInstance.get("/geo/communes", {
          params: { codePostal: zip },
        });
        if (cancel) return;
        const arr = Array.isArray(data) ? data : [];
        setVilles(arr.map((c) => c.nom).filter(Boolean));
        // auto-sélection si une seule ville
        if (arr.length === 1 && !value.city) {
          onChange("city", arr[0].nom);
        }
      } catch {
        setVilles([]);
      }
    }
    run();
    return () => {
      cancel = true;
    };
  }, [zip, value.city, onChange]);

  return (
    <div className={lock ? "opacity-60 pointer-events-none" : ""}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <div className="grid md:grid-cols-2 gap-4 mt-1">
        <input
          className="border rounded-lg px-3 py-2 md:col-span-2"
          placeholder="Adresse"
          value={value.line1 || ""}
          onChange={(e) => onChange("line1", e.target.value)}
        />
        <input
          className="border rounded-lg px-3 py-2 md:col-span-2"
          placeholder="Complément"
          value={value.line2 || ""}
          onChange={(e) => onChange("line2", e.target.value)}
        />
        <input
          className="border rounded-lg px-3 py-2"
          placeholder="Code postal"
          inputMode="numeric"
          value={value.zip || ""}
          onChange={(e) =>
            onChange(
              "zip",
              e.target.value.replace(/[^\dA-Za-z]/g, "").slice(0, 10)
            )
          }
        />
        {villes.length > 0 ? (
          <select
            className="border rounded-lg px-3 py-2"
            value={value.city || ""}
            onChange={(e) => onChange("city", e.target.value)}
          >
            <option value="">
              {villes.length > 1 ? "— Sélectionner —" : "—"}
            </option>
            {villes.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Ville"
            value={value.city || ""}
            onChange={(e) => onChange("city", e.target.value)}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Profil() {
  // state
  const [user, setUser] = useState(initialUser);
  const [company, setCompany] = useState(initialCompany);
  const [billingSame, setBillingSame] = useState(true);

  const [savingUser, setSavingUser] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  const [deletingEmail, setDeletingEmail] = useState(false); // ⬅️ AJOUT

  // load
  useEffect(() => {
    (async () => {
      // -------- Profil utilisateur --------
      try {
        const meRes = await axiosInstance.get("/auth/me");
        const me = meRes.data || {};
        setUser((u) => ({
          ...u,
          ...me,
          address: { ...u.address, ...(me.address || {}) },
        }));
      } catch {
        /* noop */
      }

      // -------- Société --------
      try {
        const soRes = await axiosInstance.get("/societe");
        const so = soRes.data || {};

        // On reconstruit les blocs d'adresse à partir des colonnes SQL
        const siege = {
          line1: so.adresse || "",
          line2: so.adresse2 || "",
          zip: so.code_postal || "",
          city: so.ville || "",
          country: so.pays || "FR",
        };

        const billing = {
          line1: so.adresse_facturation || siege.line1,
          line2: so.adresse_facturation2 || siege.line2,
          zip: so.code_postal_facturation || siege.zip,
          city: so.ville_facturation || siege.city,
          country: so.pays_facturation || siege.country,
        };

        setCompany((c) => ({
          ...c,
          nom: so.nom || "",
          siret: so.siret || "",
          ape: so.ape || "",
          tva_intra: so.tva_intra || "",
          forme_juridique: so.forme_juridique || "",
          siege: { ...c.siege, ...siege },
          billing: { ...c.billing, ...billing },
        }));

        setBillingSame(
          JSON.stringify(billing) === JSON.stringify(siege)
        );
      } catch {
        /* noop */
      }
    })();
  }, []);

  // sync billing when checked
  useEffect(() => {
    if (!billingSame) return;
    setCompany((c) => ({ ...c, billing: { ...c.siege } }));
  }, [
    billingSame,
    company.siege.line1,
    company.siege.line2,
    company.siege.zip,
    company.siege.city,
    company.siege.country,
  ]);

  // actions
  const saveUser = async () => {
    setSavingUser(true);
    try {
      const addr = {
        line1: (user.address.line1 || "").trim(),
        line2: (user.address.line2 || "").trim(),
        zip: (user.address.zip || "").trim(),
        city: (user.address.city || "").trim(),
        country: user.address.country || "FR",
      };

      const payload = {
        first_name: (user.first_name || "").trim(),
        last_name: (user.last_name || "").trim(),
        email: (user.email || "").trim(),
        phone: (user.phone || "").trim(),
        address: addr,
        // compat éventuelle avec une API à champs plats
        address_line1: addr.line1,
        address_line2: addr.line2,
        address_zip: addr.zip,
        address_city: addr.city,
        address_country: addr.country,
      };

      await axiosInstance.put("/auth/me", payload);
      notify.success({ title: "Profil", message: "Profil mis à jour." });
    } catch {
      notify.error({ title: "Profil", message: "Échec de la mise à jour." });
    } finally {
      setSavingUser(false);
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const siege = {
        line1: (company.siege.line1 || "").trim(),
        line2: (company.siege.line2 || "").trim(),
        zip: (company.siege.zip || "").trim(),
        city: (company.siege.city || "").trim(),
        country: company.siege.country || "FR",
      };

      const billing = billingSame
        ? { ...siege }
        : {
            line1: (company.billing.line1 || "").trim(),
            line2: (company.billing.line2 || "").trim(),
            zip: (company.billing.zip || "").trim(),
            city: (company.billing.city || "").trim(),
            country: company.billing.country || "FR",
          };

      const payload = {
        nom: (company.nom || "").trim(),
        siret: (company.siret || "").trim(),
        ape: (company.ape || "").trim(),
        tva_intra: (company.tva_intra || "").trim(),
        forme_juridique: (company.forme_juridique || "").trim(),
        siege,
        billing,
        // compat avec le reste de l’app (infosSociete, en-têtes, footers…)
        adresse: siege.line1,
        code_postal: siege.zip,
        ville: siege.city,
        adresse_facturation: billing.line1,
        code_postal_facturation: billing.zip,
        ville_facturation: billing.city,
      };

      await axiosInstance.put("/societe", payload);
      notify.success({ title: "Société", message: "Informations mises à jour." });
    } catch {
      notify.error({ title: "Société", message: "Échec de la mise à jour." });
    } finally {
      setSavingCompany(false);
    }
  };

  const savePassword = async () => {
    if (!pw.next || pw.next !== pw.confirm) {
      notify.warning({
        title: "Mot de passe",
        message: "Les mots de passe ne correspondent pas.",
      });
      return;
    }
    if (pw.next.length < 10) {
      notify.warning({
        title: "Mot de passe",
        message: "Minimum 10 caractères.",
      });
      return;
    }
    setSavingPw(true);
    try {
      await axiosInstance.post("/auth/me/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });

      notify.success({ title: "Sécurité", message: "Mot de passe mis à jour." });
      setPw({ current: "", next: "", confirm: "" });
    } catch {
      notify.error({
        title: "Sécurité",
        message: "Impossible de changer le mot de passe.",
      });
    } finally {
      setSavingPw(false);
    }
  };

  // ⚠️ Suppression de l'adresse e-mail (exigence Play Store)
  const handleDeleteEmail = async () => {
    if (!user.email) {
      notify.warning({
        title: "Adresse e-mail",
        message: "Aucune adresse e-mail n’est enregistrée sur votre compte.",
      });
      return;
    }

    const ok = window.confirm(
      "Cette action va supprimer définitivement votre adresse e-mail de votre compte FlowDevis.\n\n" +
      "- Vous ne recevrez plus d’e-mails de la part de FlowDevis (factures, relances, etc.).\n" +
      "- Vous devrez saisir une nouvelle adresse si vous voulez à nouveau recevoir des e-mails.\n\n" +
      "Confirmez-vous la suppression de votre adresse e-mail ?"
    );
    if (!ok) return;

    setDeletingEmail(true);
    try {
      // À implémenter côté backend : endpoint dédié pour effacer l'e-mail
      // (par ex. mettre email = NULL ou valeur anonymisée)
      await axiosInstance.post("/auth/me/remove-email");

      setUser((u) => ({ ...u, email: "" }));
      notify.success({
        title: "Adresse e-mail",
        message: "Votre adresse e-mail a été supprimée de votre compte.",
      });
    } catch {
      notify.error({
        title: "Adresse e-mail",
        message: "Impossible de supprimer l’adresse e-mail pour le moment.",
      });
    } finally {
      setDeletingEmail(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="p-6 space-y-8">
      <h1 className="text-xl font-semibold">Mon profil</h1>

      {/* -------- Utilisateur -------- */}
      <section className="rounded-2xl border bg-white shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold">Informations personnelles</h2>
            <p className="text-xs text-slate-500">
              Ces informations pourront apparaître sur vos documents.
            </p>
          </div>
          <button onClick={saveUser} disabled={savingUser} className={primaryBtn}>
            {savingUser ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        <div className="p-5 grid gap-4 md:grid-cols-2">
          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Prénom"
            value={user.first_name}
            onChange={(e) => onObj(setUser, "first_name", e.target.value)}
          />
          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Nom"
            value={user.last_name}
            onChange={(e) => onObj(setUser, "last_name", e.target.value)}
          />
          <input
            className="border rounded-lg px-3 py-2 md:col-span-2"
            placeholder="E-mail"
            value={user.email}
            onChange={(e) => onObj(setUser, "email", e.target.value)}
          />
          <input
            className="border rounded-lg px-3 py-2 md:col-span-2"
            placeholder="Téléphone (+33…)"
            value={user.phone}
            onChange={(e) => onObj(setUser, "phone", e.target.value)}
          />

          {/* Adresse utilisateur avec auto-ville */}
          <div className="md:col-span-2">
            <AdresseBloc
              label="Adresse postale"
              value={user.address}
              onChange={(k, v) => onSub(setUser, "address", k, v)}
            />
          </div>
        </div>
      </section>

      {/* -------- Sécurité -------- */}
      <section className="rounded-2xl border bg-white shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold">Sécurité</h2>
            <p className="text-xs text-slate-500">Changer votre mot de passe.</p>
          </div>
          <button
            onClick={savePassword}
            disabled={savingPw}
            className={primaryBtn}
          >
            {savingPw ? "Mise à jour…" : "Mettre à jour"}
          </button>
        </div>

        <div className="p-5 grid gap-4 md:grid-cols-3">
          <input
            type="password"
            className="border rounded-lg px-3 py-2"
            placeholder="Mot de passe actuel"
            value={pw.current}
            onChange={(e) => setPw({ ...pw, current: e.target.value })}
          />
          <input
            type="password"
            className="border rounded-lg px-3 py-2"
            placeholder="Nouveau mot de passe"
            value={pw.next}
            onChange={(e) => setPw({ ...pw, next: e.target.value })}
          />
          <input
            type="password"
            className="border rounded-lg px-3 py-2"
            placeholder="Confirmer"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
          />
          <p className="text-xs text-slate-500 md:col-span-3">
            Minimum 10 caractères, mélange recommandé de lettres, chiffres et
            symboles.
          </p>
        </div>
      </section>

      {/* -------- Société -------- */}
      <section className="rounded-2xl border bg-white shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold">Société</h2>
            <p className="text-xs text-slate-500">
              Renseignez le strict nécessaire. Utilisez la recherche INSEE pour
              aller plus vite.
            </p>
          </div>
          <button
            onClick={saveCompany}
            disabled={savingCompany}
            className={primaryBtn}
          >
            {savingCompany ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        <div className="p-5 grid gap-4">
          {/* Recherche INSEE */}
          <div className="grid gap-2">
            <label className="text-sm font-medium">Raison sociale</label>
            <InseeAutocomplete
              value={company.nom}
              onChange={(v) => onObj(setCompany, "nom", v)}
              onSelect={(it) => {
                setCompany((c) => {
                  const next = { ...c };
                  next.nom = it?.nom || next.nom;
                  if (it?.siret) next.siret = it.siret;
                  if (it?.ape) next.ape = it.ape;
                  else if (it?.code_naf) next.ape = it.code_naf;
                  if (it?.tva_intra) next.tva_intra = it.tva_intra;
                  if (it?.forme_juridique) next.forme_juridique = it.forme_juridique;
                  if (it?.adresse) {
                    next.siege = {
                      ...next.siege,
                      line1: it.adresse.line1 || next.siege.line1,
                      zip: it.adresse.zip || next.siege.zip,
                      city: it.adresse.city || next.siege.city,
                      country: it.adresse.country || next.siege.country || "FR",
                    };
                  }
                  return next;
                });
                if (billingSame)
                  setCompany((c) => ({ ...c, billing: { ...c.siege } }));
              }}
              placeholder="Rechercher par nom (ex: michelin, orange…) — min. 3 lettres"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="N° SIRET"
              value={company.siret}
              onChange={(e) => onObj(setCompany, "siret", e.target.value)}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Code APE / NAF"
              value={company.ape}
              onChange={(e) => onObj(setCompany, "ape", e.target.value)}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="N° TVA intracommunautaire"
              value={company.tva_intra}
              onChange={(e) => onObj(setCompany, "tva_intra", e.target.value)}
            />
            {/* Forme juridique: datalist riche + saisie libre */}
            <div className="relative">
              <input
                list="formes-juridiques"
                className="border rounded-lg px-3 py-2 w-full"
                placeholder="Forme juridique (choisir ou saisir)"
                value={company.forme_juridique}
                onChange={(e) =>
                  onObj(setCompany, "forme_juridique", e.target.value)
                }
              />
              <datalist id="formes-juridiques">
                {FORMES_JURIDIQUES.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Adresse siège (avec villes auto) */}
          <AdresseBloc
            label="Adresse du siège"
            value={company.siege}
            onChange={(k, v) => onSub(setCompany, "siege", k, v)}
          />

          {/* Adresse facturation */}
          <div className="flex items-center justify-between mt-2">
            <label className="text-sm font-medium">
              Adresse de facturation (optionnelle)
            </label>
            <label className="text-sm inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={billingSame}
                onChange={(e) => setBillingSame(e.target.checked)}
              />
              identique au siège
            </label>
          </div>
          <AdresseBloc
            value={company.billing}
            onChange={(k, v) => onSub(setCompany, "billing", k, v)}
            lock={billingSame}
          />
        </div>
      </section>

      {/* -------- Bas de page : bouton légal suppression e-mail + abonnement -------- */}
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500 max-w-xl">
          Si vous le souhaitez, vous pouvez supprimer définitivement votre adresse e-mail
          de votre compte FlowDevis. Votre compte restera utilisable, mais vous ne
          recevrez plus aucun e-mail (notifications, relances, reçus, etc.) tant que
          vous n’aurez pas saisi une nouvelle adresse.
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleDeleteEmail}
            disabled={deletingEmail}
            className={dangerBtn}
          >
            {deletingEmail ? "Suppression…" : "Supprimer mon compte"}
          </button>

          <button
            onClick={() => window.location.assign("/settings/abonnement")}
            className={primaryBtn}
          >
            Aller à l’abonnement
          </button>
        </div>
      </div>
    </div>
  );
}
